import React, { useState, useEffect } from "react";
import "../JobPosting/jobs.scss";
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Snackbar } from "@mui/material";
import { fetchJobById, } from "../../Redux/ActionCreators/jobsAction";
import { fetchQualifyingQuestions, fetchQualifyingQuestionById, saveQualifyingQuestion, updateQualifyingQuestion, deleteQualifyingQuestion } from "../../Redux/ActionCreators/jobsAction";
import { updateHiringAssistaniData } from "../../Redux/ActionCreators/qualifyQuestionsAction";

const PRE_SCREENING_STAGE_ID = "3dab71cf-c680-4983-a109-1cff0953999b";
// Prior to a bug fix, passing_score / enable_evaluation were saved under the Voice Call
// stage id below. Kept as a read-only fallback so old jobs still display their value;
// all writes must target PRE_SCREENING_STAGE_ID only.
const LEGACY_VOICE_STAGE_ID = "fd40e3d2-38a9-4cef-937a-93ef6a42d045";

const PreScreening = ({ setType, jobId: propJobId }) => {
  const { jobId: paramsJobId } = useParams();
  const jobId = propJobId || paramsJobId;
  const dispatch = useDispatch();

  const questions = useSelector((state) => state.jobs.qualifyingQuestions || []);
  const job = useSelector((state) => state.jobs.latestJob);

  const [newQuestion, setNewQuestion] = useState({
    question: "",
    type: "",
    score: "",
    choices: ["", ""],
    correctAnswer: "",
    is_required: false,
    enable_evaluation: false,
  });

  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passingScore, setPassingScore] = useState(0);
  const [originalScore, setOriginalScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [enableEvaluation, setEnableEvaluation] = useState(false);
  const [evaluationSaving, setEvaluationSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const loadQuestions = async () => {
    setLoading(true);
    await dispatch(fetchQualifyingQuestions(jobId));
    setLoading(false);
  };

  useEffect(() => {
    if (jobId) {
      dispatch(fetchJobById(jobId));
    }
  }, [jobId]);

  useEffect(() => {
    loadQuestions();
  }, [jobId, dispatch]);

  useEffect(() => {
    if (job?.hiring_stages) {
      const stage = job.hiring_stages.find(
        (s) => s.hiring_stage_id === PRE_SCREENING_STAGE_ID
      );
      const legacyStage = job.hiring_stages.find(
        (s) => s.hiring_stage_id === LEGACY_VOICE_STAGE_ID
      );
      const score = stage?.data?.passing_score ?? legacyStage?.data?.passing_score ?? 0;
      setPassingScore(score);
      setOriginalScore(score);
      setEnableEvaluation(stage?.data?.enable_evaluation ?? legacyStage?.data?.enable_evaluation ?? false);
    }
  }, [job]);

  useEffect(() => {
    if (questions.length > 0) {
      const total = questions.reduce((sum, q) => sum + (parseInt(q.score, 10) || 0), 0);
      setTotalScore(total);
    } else {
      setTotalScore(0);
    }
  }, [questions]);

  const resetForm = () => {
    setNewQuestion({
      question: "",
      type: "",
      score: "",
      choices: ["", ""],
      correctAnswer: "",
      is_required: false,
      enable_evaluation: false,
    });
    setEditIndex(null);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddChoice = () => {
    setNewQuestion({
      ...newQuestion,
      choices: [...newQuestion.choices, ""],
    });
  };

  const handleChoiceChange = (value, index) => {
    const updated = [...newQuestion.choices];
    if (typeof updated[index] === "object") {
      updated[index] = { ...updated[index], option_text: value };
    } else {
      updated[index] = { option_text: value };
    }
    setNewQuestion({ ...newQuestion, choices: updated });
  };

  const handleAddQuestion = () => {
    setNewQuestion({
      question: "",
      type: "",
      score: "",
      choices: ["", ""],
      correctAnswer: "",
      is_required: false,
      enable_evaluation: enableEvaluation,
    });
    setEditIndex(null);
    setShowForm(true);
  };

  const handleEdit = async (id) => {
    setLoading(true);
    const data = await dispatch(fetchQualifyingQuestionById(jobId, id));
    setLoading(false);
    if (!data) return;

    const questionType = Object.keys(QUESTION_TYPE_IDS).find(
      key => QUESTION_TYPE_IDS[key] === data.question_type_id
    );

    const mappedQuestion = {
      question: data.question_text,
      type: questionType,
      score: data.score,
      choices: data.options?.map(opt => ({ id: opt.id, option_text: opt.option_text })) || [{ option_text: "" }, { option_text: "" }],
      correctAnswer:
        ["Numeric", "Short answer"].includes(questionType)
          ? data.meta_data?.required_answer_text || ""
          : data.meta_data?.required_option_text || "",
      is_required: data.is_required,
      enable_evaluation: !!data.meta_data?.enable_evaluation,
    };

    setNewQuestion(mappedQuestion);
    setEditIndex(questions.findIndex(q => q.id === id));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    const success = await dispatch(deleteQualifyingQuestion(jobId, id));
    if (success) {
      await loadQuestions(jobId);
      showSnackbar("Question deleted successfully", "info");
    }
    setLoading(false);
  };

  const QUESTION_TYPE_IDS = {
    "MCQ": "4c524f2f-48de-493e-894f-4f0bd931b4c9",
    "Numeric": "64468292-d741-4e52-893f-0846dd56830d",
    "Yes/No": "4ffc711b-2f10-470b-b162-85f2a2f1e220",
    "Short answer": "30afb159-85d7-490d-afcd-1af9497531ae",
  };

  const QUESTION_TYPE_ID_TO_LABEL = {
    "4c524f2f-48de-493e-894f-4f0bd931b4c9": "MCQ",
    "64468292-d741-4e52-893f-0846dd56830d": "Numeric",
    "4ffc711b-2f10-470b-b162-85f2a2f1e220": "Yes/No",
    "30afb159-85d7-490d-afcd-1af9497531ae": "Short answer",
  };

  const buildQuestionPayload = (question, isEdit = false) => {
    let meta_data = { enable_evaluation: !!question.enable_evaluation };
    if (question.enable_evaluation) {
      if (question.type === "Numeric" || question.type === "Short answer") {
        meta_data = { ...meta_data, required_answer_text: question.correctAnswer };
      } else {
        meta_data = { ...meta_data, required_option_text: question.correctAnswer };
      }
    }

    let options = [];

    if (question.type === "MCQ") {
      options = question.choices
        .filter(Boolean)
        .map(opt =>
          typeof opt === "object"
            ? { id: opt.id, option_text: opt.option_text }
            : { option_text: opt }
        );
    }

    if (question.type === "Yes/No" && !isEdit) {
      options = [
        { option_text: "Yes" },
        { option_text: "No" }
      ];
    }

    return {
      question_text: question.question,
      display_order: 0,
      is_required: !!question.is_required,
      is_active: true,
      score: question.enable_evaluation ? (parseInt(question.score, 10) || 0) : 0,
      question_type_id: QUESTION_TYPE_IDS[question.type],
      meta_data,
      options
    };
  };

  const validateAndSave = async () => {
    if (!newQuestion.type) {
      showSnackbar("You have to select any one of the question types!", "error");
      return;
    }
    if (!newQuestion.question.trim()) {
      showSnackbar("Question field is mandatory!", "error");
      return;
    }
    if (newQuestion.enable_evaluation) {
      if (!newQuestion.score) {
        showSnackbar("Score field is mandatory!", "error");
        return;
      }
      if (newQuestion.type === "Short answer" && newQuestion.correctAnswer.length > 50) {
        showSnackbar("Short answer answer should be under 50 characters!", "error");
        return;
      }
      if (!newQuestion.correctAnswer?.trim()) {
        if (
          newQuestion.type === "MCQ" ||
          newQuestion.type === "Numeric" ||
          newQuestion.type === "Yes/No" ||
          newQuestion.type === "Short answer"
        ) {
          showSnackbar("Suggested / Correct Answer is mandatory!", "error");
          return;
        }
      }
      if (newQuestion.type === "MCQ") {
        const choiceTexts = newQuestion.choices
          .map(c => (typeof c === "object" ? c.option_text : c))
          .filter(Boolean);
        if (!choiceTexts.includes(newQuestion.correctAnswer)) {
          showSnackbar("Suggested / Correct Answer is mandatory!", "error");
          return;
        }
      }
    }

    setSaveLoading(true);
    const payload = buildQuestionPayload(newQuestion, editIndex !== null);

    const success = editIndex !== null
      ? await dispatch(updateQualifyingQuestion(jobId, questions[editIndex].id, payload))
      : await dispatch(saveQualifyingQuestion(jobId, payload));

    if (success) {
      await loadQuestions(jobId);
      resetForm();
      setShowForm(false);
      showSnackbar("Question saved successfully!", "success");
    }

    setSaveLoading(false);
  };

  // Merges `patch` into the Pre-Screening stage's existing data instead of
  // replacing it wholesale, so one setting's save can never wipe another's
  // (e.g. saving passing score used to blow away enable_evaluation).
  const savePreScreeningStageData = async (patch) => {
    const stage = job?.hiring_stages?.find(
      (s) => s.hiring_stage_id === PRE_SCREENING_STAGE_ID
    );
    if (!stage?.id) {
      showSnackbar("Pre-Screening stage not found for this job", "error");
      return { success: false };
    }

    try {
      const { assistant_id, ...restData } = stage.data || {};
      const payload = { data: { ...restData, ...patch } };
      const result = await dispatch(updateHiringAssistaniData(job.id, stage.id, payload));

      if (result?.success !== false) {
        dispatch(fetchJobById(jobId)); // refresh
        return { success: true };
      }
      showSnackbar(result?.message || "Failed to update Pre-Screening settings");
      return { success: false };
    } catch (err) {
      showSnackbar("Error while updating Pre-Screening settings");
      return { success: false };
    }
  };

  const handleToggleEvaluation = async (checked) => {
    setEnableEvaluation(checked);
    setEvaluationSaving(true);
    const { success } = await savePreScreeningStageData({ enable_evaluation: checked });
    if (success) {
      showSnackbar("Evaluation setting updated successfully!", "success");
    } else {
      setEnableEvaluation(!checked);
    }
    setEvaluationSaving(false);
  };

  const handleBack = () => {
    if (showForm) {
      resetForm();
      setShowForm(false);
      loadQuestions();
    } else {
      setType("");
    }
  }

  const handleSavePassingScore = async () => {
    setSaveLoading(true);
    const { success } = await savePreScreeningStageData({ passing_score: passingScore });
    if (success) {
      showSnackbar("Passing score updated successfully!", "success");
      setOriginalScore(passingScore);
    }
    setSaveLoading(false);
  };

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => { handleBack() }}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Pre-Screening</h3>
          <p>{questions.length} questions, Passing score:
            {job?.hiring_stages?.find((stage) => stage.hiring_stage_id === PRE_SCREENING_STAGE_ID)?.data?.passing_score
              ?? job?.hiring_stages?.find((stage) => stage.hiring_stage_id === LEGACY_VOICE_STAGE_ID)?.data?.passing_score
              ?? "N/A"}</p>
        </div>
        {!showForm && (
          <div className="job-settings-head-evaluation" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ margin: 0 }}>evaluation</label>
            <label className={`toggle-switch toggle-pink${enableEvaluation ? " checked" : ""}`}>
              <input
                type="checkbox"
                checked={enableEvaluation}
                disabled={evaluationSaving}
                onChange={(e) => handleToggleEvaluation(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
          </div>
        )}
      </div>

      {/* Show questions list if not in form */}
      {!showForm && (
        <div className="job-settings-body">
          {loading ? (
            <div
              className="loading-overlay"
              style={{
                position: "relative",
                marginTop: 16,
                alignItems: "start",
                height: '32px'
              }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {questions.length === 0 ? (
                <p style={{ marginTop: "14px", color: "#666", marginBottom: '20px' }}>
                  There are no Pre-Screening questions. You can add by clicking <b>Add question</b>.
                </p>
              ) : (
                questions.map((q, index) => (
                  <div className="job-settings-body-item" key={index}>
                    <p>{q.question_text}</p>
                    <h6>{QUESTION_TYPE_ID_TO_LABEL[q.question_type_id] || "Unknown"}</h6>
                    <h6>Score: {q.score}</h6>
                    <div className="job-settings-body-icons">
                      <button onClick={() => handleEdit(q.id)} title="Edit">
                        <img className="edit-icon" src="/images/square-pen-black.svg" alt="edit" width={24} height={24} />
                      </button>
                      <button onClick={() => handleDelete(q.id)} title="Delete">
                        <img className="delete-icon" src="/images/trash-bin-black.svg" alt="delete" width={24} height={24} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}

      {/* Add question button */}
      {!showForm && (
        <button className="add-question-btn" onClick={handleAddQuestion}>
          + Add question
        </button>
      )}

      {/* Question form */}
      {showForm && (
        <div className="job-settings-form">
          <div className="job-settings-form-header">
            <select
              value={newQuestion.type}
              disabled={editIndex !== null}
              onChange={(e) =>
                setNewQuestion({ ...newQuestion, type: e.target.value })
              }
            >
              <option value="">Select question type</option>
              <option value="MCQ">MCQ</option>
              <option value="Numeric">Numeric</option>
              <option value="Yes/No">Yes or No</option>
              <option value="Short answer">Short answer</option>
            </select>
            {enableEvaluation && (
              <div className="job-settings-form-labels">
                <input
                  type="checkbox"
                  checked={!!newQuestion.enable_evaluation}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, enable_evaluation: e.target.checked })
                  }
                />
                <label>evaluation</label>
              </div>
            )}
            <div className="job-settings-form-labels">
              <input
                type="checkbox"
                checked={!!newQuestion.is_required}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, is_required: e.target.checked })
                }
              />
              <label>Mandatory</label>
            </div>
          </div>
          <div className="job-settings-form-question">
            <label>Question</label>
            <textarea
              type="text"
              placeholder="Enter question"
              value={newQuestion.question}
              onChange={(e) =>
                setNewQuestion({ ...newQuestion, question: e.target.value })
              }
            />
          </div>
          {/* MCQ Answer choices */}
          {newQuestion.type === "MCQ" && (
            <div className="job-settings-form-choices">
              <label>Answer Choices</label>
              {newQuestion.choices.map((choice, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                  <input
                    type="text"
                    placeholder={`Option ${i + 1}`}
                    value={typeof choice === "object" ? choice.option_text : choice}
                    onChange={(e) => handleChoiceChange(e.target.value, i)}
                  />
                  {newQuestion.choices.length > 2 && (
                    <img
                      src="/images/cross-pink.svg"
                      alt="remove"
                      width={12}
                      height={12}
                      style={{ cursor: "pointer", marginLeft: "8px" }}
                      onClick={() => {
                        if (newQuestion.choices.length > 2) {
                          const updated = [...newQuestion.choices];
                          const deletedText = typeof updated[i] === "object" ? updated[i].option_text : updated[i];
                          updated.splice(i, 1);
                          const newCorrectAnswer = deletedText === newQuestion.correctAnswer ? "" : newQuestion.correctAnswer;
                          setNewQuestion({ ...newQuestion, choices: updated, correctAnswer: newCorrectAnswer });
                        }
                      }}
                    />
                  )}
                </div>
              ))}
              <button className="choice-btn" type="button" onClick={handleAddChoice}>
                + Add Choice
              </button>
            </div>
          )}
          {newQuestion.enable_evaluation && (
          <>
          <div className="job-settings-form-answer">
            <label>Suggested / Correct Answer</label>
            {newQuestion.type === "MCQ" && (
              <select
                value={newQuestion.correctAnswer}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                }
              >
                <option value="">Select option</option>
                {newQuestion.choices.map((c, i) => {
                  const text = typeof c === "object" ? c.option_text : c;
                  return (
                    <option key={i} value={text}>
                      {text || `Option ${i + 1}`}
                    </option>
                  );
                })}
              </select>
            )}
            {newQuestion.type === "Numeric" && (
              <input
                type="number"
                min="0"
                max="100"
                value={newQuestion.correctAnswer}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                }
              />
            )}
            {newQuestion.type === "Yes/No" && (
              <select
                value={newQuestion.correctAnswer}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                }
              >
                <option value="">Select answer</option>
                {editIndex !== null ? (
                  newQuestion.choices
                    .filter(c => (typeof c === "object" ? c.option_text : c)?.trim())
                    .map((c, i) => {
                      const text = typeof c === "object" ? c.option_text : c;
                      return <option key={i} value={text}>{text}</option>;
                    })
                ) : (
                  <>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </>
                )}
              </select>
            )}
            {newQuestion.type === "Short answer" && (
              <div className="d-flex flex-column gap-2">
                <input
                  className="text-input"
                  type="text"
                  // maxLength={50}
                  placeholder="Enter suggested answer (max 50 characters)"
                  value={newQuestion.correctAnswer}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                  }
                />
                {newQuestion.correctAnswer.length > 50 ? (
                  <small style={{ color: 'red' }}>
                    Please summarize your answer in 50 characters only
                  </small>
                ) : (
                  <small style={{ color: '#777' }}>
                    Please enter text in 50 characters only
                  </small>
                )}
              </div>
            )}
          </div>
          <div className="job-settings-form-score">
            <label>Score</label>
            <input
              type="number"
              value={newQuestion.score}
              min="0"
              max="100"
              onChange={(e) =>
                setNewQuestion({ ...newQuestion, score: e.target.value })
              }
            />
          </div>
          </>
          )}
          <div className="d-flex gap-4 align-items-center">
            <button className="save-btn" type="button" onClick={validateAndSave} disabled={saveLoading}>
              {saveLoading ? 'Saving...' : 'Save'}
            </button>
            <h6 className="cursor-pointer cancle-txt" onClick={() => { handleBack() }}>Cancel</h6>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div className="job-settings-bottom">
        <p>Set Pre-Screening Pass Score</p>
        <input
          className="number-input"
          type="number"
          min="0"
          max={totalScore}
          value={passingScore}
          onChange={(e) => {
            if (e.target.value === '') {
              setPassingScore('');
            } else {
              const value = Number(e.target.value);
              if (value <= totalScore) {
                setPassingScore(value);
              }
            }
          }}
        />
        {(passingScore !== originalScore && passingScore !== '') && (
          <button
            className="save-btn"
            type="button"
            onClick={handleSavePassingScore}
            disabled={saveLoading}
          >
            {saveLoading ? "Saving..." : "Save"}
          </button>
        )}
        <p style={{ fontSize: "12px", color: "#666" }}>
          Max allowed: {totalScore}
        </p>
      </div>
    </div>
  );
};

export default PreScreening;